import React, { type FC, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import { useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import LockIcon from '@mui/icons-material/LockOutlined';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import moment, { type Moment } from 'moment';
import { useAuth } from 'components/Auth';
import { checkIfContractorProvider } from 'utils';

import { type IActivityDetails, type IFormByActivity, type IPreAssessment } from '../../../../interfaces';
import { FormTitleKeysMap, FormStage, WspTemplateNames, TemplateNames } from 'components/Forms/constants';
import { ActivityStatus } from 'constants/activityStatus'
import useTimezoneUtils from 'hooks/useTimezoneUtils';
import { TERMINATE_REVIEW } from 'components/ToDos/queries';
import { Card } from 'components/Shared/Card';
import { LoadingCard } from 'components/Shared';
import { removeDuplicateSOU } from 'components/CaseManagement/utils/formStatusUtils';
import { useIsEmergencyContactEnabled } from 'hooks/useIsEmergencyContactEnabled';
import { COUNTRY_CODE, getAppRegionCode } from 'utils/regionUtils';

import './ClinicalRecordsTab.scss';
import { ActivityConsentFormList } from '../../ActivityDocumentForm/ActivityConsentFormList';

interface ClinicalRecordsTabProps {
    activity?: IActivityDetails;
    forms?: IFormByActivity[];
    preAssessments?: IPreAssessment[];
    fromToDo?: boolean;
}

const orderBySOUFirst = (forms: IFormByActivity[]) => {
    const index = forms.findIndex((form) => form.templateName.toLowerCase().includes("statementofunderstanding"));
    const newForms = [...forms]
    if (index > 0) {
        const [SOU] = newForms.splice(index, 1);
        newForms.unshift(SOU);
    }
    return newForms;
}

export const ClinicalRecordsTab: FC<ClinicalRecordsTabProps> = ({ activity, forms, preAssessments, fromToDo }) => {
    const { t, ready: translationsReady } = useTranslation('assessments', { useSuspense: false });

    const { formatDate, formatToProviderTime: formatTime } = useTimezoneUtils();
    const { enqueueSnackbar } = useSnackbar();
    const { activityId } = activity ?? {};
    // const navigate = useNavigate();
    const emergencyContactConfirmed = activity?.client?.contactAddress?.emergencyContactDeclined !== null;
    const addressConfirmed = activity?.client?.contactAddress?.clientAddressDeclined !== null;
    const isNewActivity = useIsEmergencyContactEnabled(activity!);
    const { effectiveProviderData } = useAuth();

    const sortedForms = forms ? orderBySOUFirst(forms.filter(removeDuplicateSOU(forms))) : [];

    const [terminateToDo] = useMutation(TERMINATE_REVIEW, {
            context: {skipGlobalErrorHandling: true},
            onError: (error) => {
                console.error(error);
            },
            onCompleted: () => {
                enqueueSnackbar(t('pre_assessments_already_reviewed'), {variant: 'error'});
            }
        }
    )

    const getPreAssessmentFormUrl = (pa) => {
        return `/preassessments/${activityId}${
            pa.additionalIndividualId
                ? '/' + pa.additionalIndividualId
                : ''
        }`;
    }

    const closeAlreadyReviewedPreAssessments = () => {
        if (fromToDo)
        {
            let toClose = preAssessments?.find(pa => pa.status === FormStage.PreAssessmentReviewed && pa.additionalIndividualId === 0);
            if (toClose && activityId)
            {
                terminateToDo({ variables: { activityId: activityId } })            
            }
        }
    }

    const getFormattedTimestamp = (form) => {
        const date = new Date(form.updatedOn || form.createdOn);
        return `${formatDate(date, 'ddd ll')} | ${formatTime(date)}`;
    }    


    const consentFormIsVisible = (activity) => {  
        let isWithin30DaysAfterCloseDate = false;
        if (activity?.activityClosedTime) {
            let caseAvailableUntil: Moment | undefined = undefined;
                caseAvailableUntil = 
                activity.timezoneOlsonName
                    ? moment.tz(activity.activityClosedTime, activity.timezoneOlsonName)
                    : moment(activity.activityClosedTime);
                caseAvailableUntil.add(30, 'days');

                if (caseAvailableUntil.isSameOrAfter(moment(), 'day')) {
                    isWithin30DaysAfterCloseDate = true;
                }            
        }

        return activity.activityStatusCode === ActivityStatus.Active || 
               (checkIfContractorProvider(effectiveProviderData) && isWithin30DaysAfterCloseDate);
    }

    const consentFormIsReadonly = (activity) => {
        return activity.activityStatusCode !== ActivityStatus.Active;
    }

    useEffect(() => {
        closeAlreadyReviewedPreAssessments();                
    }, [activityId, preAssessments, fromToDo]);

    const renderFormLink = (form: IFormByActivity) => {
        const linkDisabled = !(emergencyContactConfirmed && addressConfirmed) && isNewActivity && !form.templateName.startsWith(TemplateNames.StatementOfUnderstanding)
        
        return (
            form.templateName === WspTemplateNames.InitialAssessmentReport ? (
                <Link to={{ pathname: `/clients/${activityId}/reports/${form.id}` }}>
                    {t(`${FormTitleKeysMap[form.templateName]}`, { replace: { sessionId: form.sessionId } })}
                </Link>)
                : (
                    linkDisabled ? (
                        <span title={t('client_contact_update_required')}><LockIcon />{t(`${FormTitleKeysMap[form.templateName]}`, { replace: { sessionId: form.sessionId } })}</span>
                    )
                    : (<Link to={linkDisabled ? { hash: '' } : { pathname: `/forms/${form.id}` }}>
                        {t(`${FormTitleKeysMap[form.templateName]}`, { replace: { sessionId: form.sessionId } })}
                    </Link>)
                )
        );
    }

    return (
        <Container maxWidth="md" className="activity_details_documents_container">
            <Card elevation={1} className="clinical_records_paper">
                {
                    preAssessments && preAssessments.length > 0 &&
                    <>
                        <h2>{t('pre-assessments')}</h2>
                        <div className="clinical_records_container">
                            {
                                preAssessments.map((pa) => (
                                    <div className="clinical_records_list_item" key={pa.additionalIndividualId}>
                                        <div className="clinical_records_list_item_left">
                                            {
                                                pa.url
                                                ? (
                                                    <a href={pa.url} target="_blank" rel="noopener noreferrer" className="color--link">
                                                        {pa.clientName}
                                                    </a>
                                                )
                                                : (
                                                    <Link className="color--link" to={{pathname: getPreAssessmentFormUrl(pa)}}>
                                                        {pa.clientName}
                                                    </Link>
                                                )
                                            }
                                        </div>
                                        <div className="clinical_records_list_item_right">
                                            <div>{t(pa.status)}</div>
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </>
                }
                <h2>{t('clinical_records')}</h2>
                {!(emergencyContactConfirmed && addressConfirmed) && isNewActivity && !!sortedForms?.length && translationsReady && (
                    <Alert color='warning' icon={<WarningIcon color='error' fill='orange' />}>
                        <span>{t('in_order_to_proceed_you_need_to')}
                            <ol>
                                <li><Link to={{ pathname: `/clients/${activityId}/forms/first_appointment_assessment` }}>{t('acknowledge_statement_of_understanding')}</Link></li>
                                {!addressConfirmed && <li><Link to={{ pathname: `/clients/${activityId}/forms/contact_address` }} state={{ workflow: "ClinicalRecords" }}>{t('update_confirm_client_contact_info')}</Link></li>}
                                {!emergencyContactConfirmed && <li><Link to={{ pathname: `/clients/${activityId}/forms/emergency_contact` }} state={{ workflow: "ClinicalRecords" }}>{t('update_confirm_emergency_contact_info')}</Link></li>}
                            </ol>
                        </span>
                    </Alert>
                )}
                <div className="clinical_records_container">
                    {
                        sortedForms?.map((form) => !(form.templateName === TemplateNames.SessionNote && form.updatedOn === null) && (
                            <div className="clinical_records_list_item" key={form.id}>
                                <div className="clinical_records_list_item_left">
                                    {renderFormLink(form)}
                                    <p className="clinical_record_timestamp">
                                        {getFormattedTimestamp(form)}
                                    </p>
                                </div>
                                <div className="clinical_records_list_item_right">
                                    <p>
                                        {t('FormUpdateBy-label', { replace: { name: form.updatedBy || form.createdBy }})}
                                    </p>
                                </div>
                            </div>
                        ))
                    }
                </div>
            </Card>
            {(getAppRegionCode() === COUNTRY_CODE.CANADA && consentFormIsVisible(activity) &&
            <LoadingCard elevation={2} radius="regular" className="clinical_records_paper">
                { activityId ? <ActivityConsentFormList activityId={activityId} isReadOnly={consentFormIsReadonly(activity)} /> : <></> }
            </LoadingCard>)}
        </Container>
    );
};
